import { Box, css, styled, ToggleButton, ToggleButtonGroup, toggleButtonGroupClasses } from '@mui/material';
import { getRouteApi, useLocation } from '@tanstack/react-router';
import React from 'react';

import { navItems } from '@/data/navItems';
import { useUIStore } from '@/stores/useUIStore';
import { transientOptions } from '@/utils/transient-options';

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(2)};
    align-items: center;

    &
      .${toggleButtonGroupClasses.firstButton},
      &
      .${toggleButtonGroupClasses.middleButton},
      &
      .${toggleButtonGroupClasses.lastButton} {
      border-radius: ${theme.spacing(1)};
    }
  `,
);

const StyledToggleButton = styled(
  ToggleButton,
  transientOptions,
)<{ $expanded: boolean }>(
  ({ theme, $expanded }) => css`
    border: none;
    padding: ${theme.spacing(1)};
    width: ${$expanded ? '100%' : '42px'};
    height: 42px;
    justify-content: ${$expanded ? 'flex-start' : 'center'};
    transition: width 0.3s;
    overflow: hidden;

    & > svg {
      width: 24px;
      height: 24px;
      flex-shrink: 0;
    }

    & > div {
      white-space: nowrap;
      overflow: hidden;
      max-width: ${$expanded ? '200px' : '0'};
      opacity: ${$expanded ? 1 : 0};
      transition:
        max-width 0.3s,
        opacity 0.3s;
    }

    &.Mui-selected {
      background-color: ${theme.palette.primary.main};
      box-shadow:
        0 1px 5px 0 rgba(0, 0, 0, 0.12),
        0 2px 2px 0 rgba(0, 0, 0, 0.14),
        0 3px 1px -2px rgba(0, 0, 0, 0.2);

      &:hover {
        background-color: ${theme.palette.primary.dark};
      }
    }
  `,
);

const routeApi = getRouteApi('/_map');

export function SidebarNav() {
  const navigate = routeApi.useNavigate();
  const { pathname } = useLocation();

  const sidebarExpanded = useUIStore((state) => state.sidebarExpanded);

  const handleChange = (_: React.MouseEvent<HTMLElement>, to: string | null) => {
    if (to) navigate({ to, search: true });
  };

  return (
    <StyledToggleButtonGroup exclusive onChange={handleChange} orientation="vertical" value={pathname}>
      {navItems.map(({ to, icon: Icon, label }) => (
        <StyledToggleButton key={to} $expanded={sidebarExpanded} size="large" value={to}>
          <Icon />
          {sidebarExpanded ? <Box ml={1}>{label}</Box> : null}
        </StyledToggleButton>
      ))}
    </StyledToggleButtonGroup>
  );
}
